import { useEffect } from 'react';

import { useAppSyncEvents } from '@/shared/hooks/use-appsync-events/use-app-sync-events';
import { TAssetOperationTypeEnum } from '@/shared/hooks/use-assets-app-sync-events/use-assets-app-sync-events.types';
import { useInvalidateAssetTags } from '@/shared/hooks/use-invalidate-asset-tags/use-invalidate-asset-tags';
import { auditApi } from '@/shared/redux/api/audit/audit-api';
import { selectUser } from '@/shared/redux/api/auth/auth-api';
import { useReadTenantQuery } from '@/shared/redux/api/ident/ident-api';
import { setBackgroundMode } from '@/shared/redux/background-refetch/background-refetch.slice';
import { useAppDispatch, useAppSelector } from '@/shared/redux/hooks/hooks';
import { AssetTypeEnum } from '@/shared/types/types.generated';
import { notifyAssetEvent } from '@/shared/utils/asset-event-wait/asset-event-wait';
import { snakeToCamelCase } from '@/shared/utils/transform';

export const useAssetsAppSyncEvents = () => {
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const { data } = useReadTenantQuery({}, { skip: !user });
  const { invalidateAssetTags } = useInvalidateAssetTags();
  const { connectAndSubscribe, disconnect } = useAppSyncEvents({});

  const tenantId = data?.readTenant?.tenantId;
  const webSocketChannel = `default/${tenantId}/assets`;

  const onMessageReceived = (data: any) => {
    const event = snakeToCamelCase(data?.event);
    const assetId = event?.assetId;
    const assetType = event?.assetType as AssetTypeEnum;
    const operationType = event?.operationType as TAssetOperationTypeEnum;
    const payload = event?.assetObject;

    if (!assetId || !assetType || !operationType) {
      console.warn('Invalid asset event data', data);
      return;
    }

    dispatch(setBackgroundMode());

    void invalidateAssetTags({
      assetType,
      assetId,
      operationType,
      payload,
    });

    // After invalidation, so a waiting caller resumes with the refetch already in flight.
    notifyAssetEvent({ assetType, assetId });

    dispatch(auditApi.util.invalidateTags([{ type: 'AuditLogs', id: 'LIST' }]));
  };

  useEffect(() => {
    if (tenantId) {
      void connectAndSubscribe(webSocketChannel, onMessageReceived);
    }
    return disconnect;
  }, [data]);
};
